import { useState } from "react";
import { Link } from "react-router-dom";
import { Link2, Unlink } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useProceedingLinkCandidates, useProceedingMutations } from "../hooks";
import {
  APPLICATION_STATUS_LABELS,
  type CaseCloseValues,
  type Proceeding,
} from "../types";
import { DetachAutoCaseModal } from "./DetachAutoCaseModal";
import { CaseOutcomeFields, useCaseOutcome } from "./CaseOutcomeFields";

// Powiązania sprawy w zakładce „Sprawy": zamówienia (zlecenie WPW prowadzące sprawę —
// auto — albo dowolne zamówienie projektu) i pakiety z modułu Wnioski. Lista z odpinaniem
// oraz picker „Powiąż" zasilany kandydatami projektu (GET …/link-candidates/). Sprawa
// bez żadnego powiązania to sprawa ręczna — też poprawna. Sprawa idzie za statusem pakietu:
// ZAKOŃCZONY domyka otwartą sprawę przy podpięciu, więc zamiast od razu wiązać pytamy
// o rozstrzygnięcie; NIEZAKOŃCZONY pod ZAMKNIĘTĄ sprawą otwiera ją ponownie (sam status),
// więc pytamy o potwierdzenie. Zamówienia wiąże się tylko z otwartą sprawą. Zakończone
// zlecenie WPW też domyka sprawę „owner", ale wynik liczy system (uzgodnienia / odmowy).
// Sprawę założoną automatycznie przy wysyłce pakietu (`founded_case`) odpięcie tego
// pakietu USUWA — tylko po ostrzeżeniu (DetachAutoCaseModal → delete_case).

// Pakiet, którego podpięcie zamknie sprawę: kandydat albo już podpięty (`relink` — stara
// para „zakończony pakiet + otwarta sprawa"; ponowne podpięcie domyka sprawę).
type ClosingPackage = { id: string; number: string; relink: boolean };
// Pakiet, którego podpięcie otworzy zamkniętą sprawę.
type ReopeningPackage = { id: string; number: string; status: string };

const CHIP =
  "text-[9px] font-mono uppercase tracking-wide px-1 py-px border border-[var(--color-border)] text-[var(--color-text-muted)] whitespace-nowrap";
const UNLINK_BTN =
  "inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-danger)] transition-colors";

// Błąd z koperty API {data, errors: {detail}} — bez zaciągania całego helpera stron.
const errorText = (e: unknown): string =>
  (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail
  ?? "Nie udało się zmienić powiązania.";

export function ProceedingLinksPanel({
  proceeding: p,
  connectionId,
}: {
  proceeding: Proceeding;
  connectionId: string;
}) {
  const m = useProceedingMutations(connectionId);
  const isOpen = p.status === "open";
  const [picking, setPicking] = useState(false);
  const [closing, setClosing] = useState<ClosingPackage | null>(null);
  const [reopening, setReopening] = useState<ReopeningPackage | null>(null);
  const [detaching, setDetaching] = useState<{ id: string; number: string } | null>(null);
  const { data: candidates, isLoading } = useProceedingLinkCandidates(p.id, picking);
  const orders = p.orders ?? [];
  const packages = p.official_applications ?? [];
  const busy = m.linkOrder.isPending || m.linkApplication.isPending;
  const error = m.linkOrder.error ?? m.linkApplication.error ?? m.unlinkOrder.error ?? m.unlinkApplication.error;

  const options = [
    ...(candidates?.orders ?? []).map((o) => ({
      value: `order:${o.order_id}`,
      label: [
        o.is_wpw ? "WPW" : "zamówienie", o.order_number, o.order_type_display, o.status_display, o.contractor_name,
        o.closes_case ? "zamknie sprawę" : null,
      ].filter(Boolean).join(" · "),
    })),
    ...(candidates?.applications ?? []).map((a) => ({
      value: `app:${a.id}`,
      label: [
        "pakiet", a.number, a.type_label, a.status_display, a.wpw_owner_name,
        a.closes_case ? "zamknie sprawę" : null,
        a.reopens_case ? "otworzy sprawę ponownie" : null,
      ].filter(Boolean).join(" · "),
    })),
  ];

  const pick = (value: string) => {
    if (!value) return;
    const [kind, id] = value.split(":");
    const done = { onSuccess: () => setPicking(false) };
    if (kind === "order") {
      m.linkOrder.mutate({ id: p.id, order: id }, done);
      return;
    }
    const pkg = candidates?.applications.find((a) => a.id === id);
    if (pkg?.closes_case) setClosing({ id: pkg.id, number: pkg.number, relink: false });
    else if (pkg?.reopens_case) setReopening({ id: pkg.id, number: pkg.number, status: pkg.status_display });
    else m.linkApplication.mutate({ id: p.id, application: id }, done);
  };

  return (
    <div className="mt-2 pt-2 border-t border-[var(--color-border-muted)]" data-testid="proceeding-links">
      <div className="flex items-center justify-between gap-2">
        <span className="section-label">Powiązania</span>
        <Button size="sm" variant="ghost" onClick={() => setPicking((v) => !v)}>
          <Link2 size={12} /> {picking ? "Anuluj" : isOpen ? "Powiąż" : "Powiąż pakiet"}
        </Button>
      </div>
      {orders.length === 0 && packages.length === 0 && !picking && (
        <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
          Sprawa ręczna — bez powiązanego wniosku ani zlecenia.
        </p>
      )}
      <ul className="space-y-1 text-[12px] font-mono">
        {orders.map((o) => (
          <li key={o.link_id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className={CHIP}>{o.is_wpw ? "WPW" : "zamówienie"}</span>
            <Link to={`/orders/${o.order_id}`} className="text-[var(--color-primary)] hover:underline">
              {o.order_number}
            </Link>
            <span className="text-[var(--color-text-secondary)]">
              {o.order_type_display} · {o.status_display}{o.contractor_name ? ` · ${o.contractor_name}` : ""}
            </span>
            {o.auto && (
              <span className={CHIP} title="Sprawa założona automatycznie przy zleceniu WPW">auto</span>
            )}
            {o.case_left_open && (
              <>
                <span className="text-amber-700">zakończone, a sprawa wciąż otwarta</span>
                {/* Wynik liczy system z uzgodnień / odmów właścicieli — bez okna z pytaniem. */}
                <Button
                  size="sm"
                  variant="ghost"
                  title="Zamknie sprawę z wynikiem z uzgodnień i odmów właścicieli"
                  loading={m.linkOrder.isPending}
                  onClick={() => m.linkOrder.mutate({ id: p.id, order: o.order_id })}
                >
                  Zamknij sprawę
                </Button>
              </>
            )}
            {isOpen && (
              <button
                type="button"
                className={UNLINK_BTN}
                title="Odepnij zamówienie od sprawy"
                aria-label={`Odepnij ${o.order_number}`}
                disabled={m.unlinkOrder.isPending}
                onClick={() => m.unlinkOrder.mutate({ id: p.id, order: o.order_id })}
              >
                <Unlink size={11} />
              </button>
            )}
          </li>
        ))}
        {packages.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className={CHIP}>pakiet</span>
            <Link to={`/power-projects/wnioski/${a.id}`} className="text-[var(--color-primary)] hover:underline">
              {a.number}
            </Link>
            <span className="text-[var(--color-text-secondary)]">
              {a.type_label} · {APPLICATION_STATUS_LABELS[a.status] ?? a.status}
            </span>
            {p.status === "closed" && a.status === "completed" && (
              <span className="text-[var(--color-text-muted)]">zakończony razem ze sprawą</span>
            )}
            {a.case_left_open && (
              <>
                <span className="text-amber-700">zakończony, a sprawa wciąż otwarta</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setClosing({ id: a.id, number: a.number, relink: true })}
                >
                  Zamknij sprawę
                </Button>
              </>
            )}
            {a.founded_case ? (
              <>
                <span className="text-[var(--color-text-muted)]">sprawa założona przy jego wysyłce</span>
                <button
                  type="button"
                  className={UNLINK_BTN}
                  title="Odepnij pakiet — sprawa założona automatycznie zostanie usunięta"
                  aria-label={`Odepnij ${a.number} i usuń sprawę`}
                  disabled={m.unlinkApplication.isPending}
                  onClick={() => {
                    m.unlinkApplication.reset();
                    setDetaching({ id: a.id, number: a.number });
                  }}
                >
                  <Unlink size={11} />
                </button>
              </>
            ) : isOpen && !a.case_left_open && (
              <button
                type="button"
                className={UNLINK_BTN}
                title="Odepnij pakiet od sprawy"
                aria-label={`Odepnij ${a.number}`}
                disabled={m.unlinkApplication.isPending}
                onClick={() => m.unlinkApplication.mutate({ id: p.id, application: a.id })}
              >
                <Unlink size={11} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {picking && (
        <div className="mt-1.5">
          {isLoading ? (
            <p className="text-[11px] text-[var(--color-text-muted)]">Ładuję kandydatów…</p>
          ) : options.length === 0 ? (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              {isOpen
                ? "Brak zamówień ani pakietów do powiązania w tym projekcie."
                : "Brak pakietów do powiązania w tym projekcie."}
            </p>
          ) : (
            <SearchableSelect
              options={options}
              value=""
              placeholder={isOpen ? "Wybierz zamówienie albo pakiet…" : "Wybierz pakiet…"}
              disabled={busy}
              onChange={(e) => pick(e.target.value)}
            />
          )}
        </div>
      )}
      {error && !closing && !reopening && (
        <p className="text-[11px] text-[var(--color-danger)]">{errorText(error)}</p>
      )}
      {detaching && (
        <DetachAutoCaseModal
          number={detaching.number}
          caseName={p.type_name}
          pending={m.unlinkApplication.isPending}
          error={m.unlinkApplication.error ? errorText(m.unlinkApplication.error) : null}
          onClose={() => setDetaching(null)}
          onConfirm={() =>
            m.unlinkApplication.mutate(
              { id: p.id, application: detaching.id, deleteCase: true },
              { onSuccess: () => setDetaching(null) },
            )
          }
        />
      )}
      {reopening && (
        <ReopenOnLinkModal
          pkg={reopening}
          proceeding={p}
          pending={m.linkApplication.isPending}
          error={m.linkApplication.error ? errorText(m.linkApplication.error) : null}
          onClose={() => setReopening(null)}
          onConfirm={() =>
            m.linkApplication.mutate(
              { id: p.id, application: reopening.id, reopen: true },
              { onSuccess: () => { setReopening(null); setPicking(false); } },
            )
          }
        />
      )}
      {closing && (
        <LinkCompletedPackageModal
          pkg={closing}
          proceeding={p}
          pending={m.linkApplication.isPending}
          error={m.linkApplication.error ? errorText(m.linkApplication.error) : null}
          onClose={() => setClosing(null)}
          onConfirm={(close) =>
            m.linkApplication.mutate(
              { id: p.id, application: closing.id, close },
              { onSuccess: () => { setClosing(null); setPicking(false); } },
            )
          }
        />
      )}
    </div>
  );
}

// Zakończony pakiet (odpowiedź urzędu już wpłynęła) podpięty pod otwartą sprawę zamyka ją
// w tym samym kroku — pakiet i sprawa to jedno postępowanie. Rozstrzygnięcia pakiet nie
// pamięta, więc wybiera je człowiek; bez domyślnego „pozytywne", bo to ono zabezpiecza tytuły.
function LinkCompletedPackageModal({ pkg, proceeding, pending, error, onClose, onConfirm }: {
  pkg: ClosingPackage;
  proceeding: Proceeding;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (v: CaseCloseValues) => void;
}) {
  const resolution = useCaseOutcome("");

  return (
    <Modal
      open
      onClose={onClose}
      title={pkg.relink ? `Zamknij sprawę pakietu ${pkg.number}` : `Podepnij zakończony pakiet ${pkg.number}`}
      size="sm"
    >
      <div className="space-y-3 text-sm">
        {pkg.relink ? (
          <p>
            Pakiet <b>{pkg.number}</b> został zakończony, ale jego sprawa została otwarta — tak
            działało „Zakończ” przed 2 września 2026. Pakiet i sprawa to jedno postępowanie, więc{" "}
            <b>zamknij sprawę „{proceeding.type_name}”</b> z rozstrzygnięciem, które wydał urząd.
          </p>
        ) : (
          <p>
            Pakiet <b>{pkg.number}</b> jest już zakończony — odpowiedź urzędu wpłynęła. Pakiet i sprawa
            to jedno postępowanie, więc podpięcie <b>zamknie sprawę „{proceeding.type_name}”</b> ze
            wskazanym rozstrzygnięciem.
          </p>
        )}
        <CaseOutcomeFields
          state={resolution}
          proceedingId={proceeding.id}
          scope={proceeding.scope}
          requireChoice
        />
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={pending}>Anuluj</Button>
          <Button
            size="sm"
            loading={pending}
            disabled={!resolution.outcome || resolution.missingParcels}
            onClick={() => onConfirm(resolution.values())}
          >
            {pkg.relink ? "Zamknij sprawę" : "Podepnij i zamknij sprawę"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Niezakończony pakiet pod ZAMKNIĘTĄ sprawą: sprawa idzie za pakietem, więc wraca do
// „otwarta" — ale tylko po potwierdzeniu i tylko status. Odpowiedź urzędu, tytuły nadane
// zamknięciem i pozostałe pakiety zostają, jak były.
function ReopenOnLinkModal({ pkg, proceeding, pending, error, onClose, onConfirm }: {
  pkg: ReopeningPackage;
  proceeding: Proceeding;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal open onClose={onClose} title={`Podepnij pakiet ${pkg.number}`} size="sm">
      <div className="space-y-3 text-sm">
        <p>
          Sprawa <b>„{proceeding.type_name}”</b> jest zamknięta
          {proceeding.outcome_display ? <> ({proceeding.outcome_display.toLowerCase()})</> : null}, a pakiet{" "}
          <b>{pkg.number}</b> nie jest zakończony ({pkg.status.toLowerCase()}). Podpięcie{" "}
          <b>otworzy sprawę ponownie</b>.
        </p>
        <p className="text-xs text-[var(--color-text-secondary)]">
          Cofamy tylko status sprawy — pozycje (np. odpowiedź urzędu), tytuły dostępu i pozostałe
          pakiety zostają bez zmian. Poprzednie zamknięcie zostaje w historii sprawy.
        </p>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={pending}>Anuluj</Button>
          <Button size="sm" loading={pending} onClick={onConfirm}>Podepnij i otwórz sprawę</Button>
        </div>
      </div>
    </Modal>
  );
}
